import React, { useEffect, useLayoutEffect, useState, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { getAllQuickLink } from "../../api/getapi";
import { deleteQuickLink, updateQuickLink } from "../../api/postapi";
import CustomizeTableview from "../TableView/CustomizeTableview";
import { toast } from "react-toastify";
import PaginationBar from "../PaginationBar/PaginationBar";
import { Form, InputGroup, Button, Modal } from "react-bootstrap";

const getFilterResolvedValues = (filter) => {
  if (Array.isArray(filter?.values)) {
    return filter.values
      .map((val) => {
        if (val && typeof val === "object" && "value" in val) {
          const resolvedValue = val.value;
          if (typeof resolvedValue === "boolean") {
            return resolvedValue ? "true" : "false";
          }
          if (resolvedValue === 1 || resolvedValue === 0) {
            return resolvedValue === 1 ? "true" : "false";
          }
          return String(resolvedValue);
        }
        return String(val);
      })
      .filter(Boolean);
  }

  const rawValue = filter?.value || filter?.filter_values || "";
  if (!rawValue) return [];

  if (typeof rawValue === "string") {
    return rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  return [String(rawValue)];
};

const CaretTextCell = ({ text, previewLength = 60 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return "—";

  const isLong = text.length > previewLength;
  const displayText = expanded || !isLong ? text : text.slice(0, previewLength) + "...";

  return (
    <div style={{ whiteSpace: "normal", textAlign: "left" }}>
      {displayText}{" "}
      {isLong && (
        <span style={{ cursor: "pointer", color: "#0d6efd", fontWeight: 500 }} onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? "\u25B2" : "\u25BC"}
        </span>
      )}
    </div>
  );
};

const ViewQuickLinks = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const rowsPerPage = 10;
  const isSearchChanging = useRef(false);
  const prevPage = useRef(1);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({ title: "", content: "", faqs: [], type: "content" });

  const openPreview = (quickLink, type = "content") => {
    setPreviewData({
      title: quickLink?.title || "",
      content: quickLink?.content || "",
      faqs: quickLink?.faqs || [],
      type,
    });
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData({ title: "", content: "", faqs: [], type: "content" });
  };

  const fetchQUickLinks = async (page = currentPage, search = searchQuery) => {
    await getAllQuickLink(page, rowsPerPage, search)
      .then((res) => {
        console.log(res?.data);
        const responseData = res?.data?.data || [];
        const total = res?.data?.pagination?.no_of_items || responseData.length;

        setTotalCount(total);

        console.log(responseData);

        setData(
          responseData?.map((data) => {
            // Group filter values by their corresponding filter names
            const groupedFilterValues = (data?.filters || [])
              .map((filter) => {
                const name = filter?.name;
                if (!name) return null;

                const valuesArray = getFilterResolvedValues(filter);

                if (!valuesArray.length) return null;

                return `${name}: ${valuesArray.join(", ")}`;
              })
              .filter(Boolean);

            const hasContent = Boolean(data?.content && data.content.trim().length > 0);
            const hasFaqs = Array.isArray(data?.faqs) && data.faqs.length > 0;

            const fullMetaDescription = data?.meta_description || "";

            const filterNamesText = (data.filters || [])
              .map((f) => f?.name)
              .filter(Boolean)
              .join(", ");

            const filterValuesText = groupedFilterValues.join(" | ");

            const ExpandedContent = (
              <div className="p-3">
                <div className="d-flex flex-wrap gap-3" style={{ rowGap: "1rem", columnGap: "1.5rem" }}>
                  <div style={{ minWidth: "260px", flex: "1 1 280px" }}>
                    <h6 className="mb-2">Description</h6>
                    <CaretTextCell text={fullMetaDescription || "—"} previewLength={400} />
                  </div>
                  <div style={{ minWidth: "260px", flex: "1 1 280px" }}>
                    <h6 className="mb-2">Filters</h6>
                    <div className="mb-1" style={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      <strong>Names:</strong> {filterNamesText || "—"}
                    </div>
                    <div style={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      <strong>Values:</strong> {filterValuesText || "—"}
                    </div>
                  </div>
                </div>
              </div>
            );

            return {
              id: data?.id,
              Slug: data?.slug,
              Title: data?.title,
              LinkText: data?.link_text,
              catrgory: data?.category_name,
              metaTitle: data?.meta_title,
              metaDescription: fullMetaDescription,
              meta_keyword: data?.meta_keyword,
              filterName: filterNamesText,
              FilterValue: filterValuesText,
              ExpandedContent,

              ContentPreview: hasContent ? (
                <Button size="sm" variant="outline-primary" onClick={() => openPreview(data, "content")}>
                  Preview Content
                </Button>
              ) : (
                "—"
              ),
              FAQPreview: hasFaqs ? (
                <Button size="sm" variant="outline-secondary" onClick={() => openPreview(data, "faq")}>
                  Preview FAQs
                </Button>
              ) : (
                "—"
              ),
              is_core: data?.is_core ? "true" : "false",
              is_active: data?.is_active,
            };
          }),
        );
      })
      .catch((e) => console.log(e));
  };

  // Initial load
  useLayoutEffect(() => {
    fetchQUickLinks(1, "");
  }, []);

  // Debounced search effect
  useEffect(() => {
    isSearchChanging.current = true;

    const delayDebounce = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      prevPage.current = 1; // Update prev page
      fetchQUickLinks(1, searchQuery);
      isSearchChanging.current = false;
    }, 500);

    return () => {
      clearTimeout(delayDebounce);
      isSearchChanging.current = false;
    };
  }, [searchQuery]);

  // Fetch data when page changes
  useEffect(() => {
    // Only fetch if page actually changed and search is not changing
    if (!isSearchChanging.current && prevPage.current !== currentPage) {
      fetchQUickLinks(currentPage, searchQuery);
      prevPage.current = currentPage;
    }
  }, [currentPage]);

  console.log(data);

  const edit = (row) => {
    navigate(`/quickLinks/edit/${row.id}`);
  };

  const remove = async (row) => {
    try {
      await deleteQuickLink(row.id);
      console.log("Quick link deleted successfully");
      toast.success("Quick link deleted successfully");
      // Refresh the data after deletion
      fetchQUickLinks(currentPage, searchQuery);
    } catch (e) {
      console.error("Error deleting quick link:", e);
      toast.error("Error deleting quick link");
    }
  };

  const handleActiveDeactive = async (row) => {
    console.log(row);
    try {
      const newStatus = !row.is_active;
      const submitData = {
        quickLink: {
          is_active: newStatus,
        },
      };

      await updateQuickLink(row.id, submitData);
      toast.success(`Quick link ${newStatus ? "activated" : "deactivated"} successfully`);
      // Refresh the data after update
      fetchQUickLinks(currentPage, searchQuery);
    } catch (error) {
      console.error("Error updating quick link status:", error);
      toast.error("Error updating quick link status");
    }
  };

  const activeDeactiveConfig = {
    className: "w-100",
    style: {},
    variant: (row) => (!row.is_active ? "success" : "danger"),
    onClick: handleActiveDeactive,
    label: (row) => (!row.is_active ? "Active" : "Inactive"),
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <>
      {/* Search Bar */}
      <div className="mb-3 px-3">
        <InputGroup>
          <Form.Control type="text" placeholder="Search by title..." value={searchQuery} onChange={handleSearchChange} />
        </InputGroup>
      </div>

      <CustomizeTableview key={data?.id} data={data} ActiveDeactive={activeDeactiveConfig} edit={edit} remove={remove} columnNotShow={["id", "is_active", "metaDescription", "filterName", "FilterValue", "ExpandedContent"]} expandRowContentKey="ExpandedContent" />

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-3">
          <PaginationBar noOfPages={totalPages} currentPage={currentPage} changePage={handlePageChange} />
        </div>
      )}

      <Modal show={showPreview} onHide={closePreview} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Preview {previewData.type === "faq" ? "FAQs" : "Content"}
            {previewData.title ? ` - ${previewData.title}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewData.type === "faq" ? (
            previewData.faqs && previewData.faqs.length > 0 ? (
              <div className="content">
                <div className="d-flex flex-column gap-3">
                  {previewData.faqs.map((faq, index) => (
                    <div key={`${faq.question}-${index}`}>
                      <p className="fw-semibold mb-1">
                        Q{index + 1}. {faq.question}
                      </p>
                      <p className="mb-0">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p>No FAQs available.</p>
            )
          ) : previewData.content ? (
            <div className="content" dangerouslySetInnerHTML={{ __html: previewData.content }} />
          ) : (
            <p>No content available.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewQuickLinks;
